import { useState, useCallback, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { getUserData, updateUserPassword, saveUserPreferences, updateUserPin } from '../../services/Users/userService';
import useUsernameFromToken from '../useUsernameFromToken';

const useProfileForm = () => {
  const [userData, setUserData] = useState({});
  const [bgColor, setBgColor] = useState('#2c3e50');
  const [textColor, setTextColor] = useState('#ffffff');
  const { register, handleSubmit, setError, clearErrors, formState: { errors } } = useForm();
  const { getUsernameFromToken } = useUsernameFromToken();

  const username = useMemo(() => getUsernameFromToken(), [getUsernameFromToken]);

  const updateCSSVariables = useCallback((bgColor, textColor) => {
    document.documentElement.style.setProperty('--bg-color', bgColor);
    document.documentElement.style.setProperty('--text-color', textColor);
  }, []);

  const fetchUserData = useCallback(async () => {
    try {
      if (!username) {
        // Retorna null silenciosamente quando não há username
        // Isso evita erros quando o contexto é montado antes do login
        return null;
      }

      const data = await getUserData(username);
      setUserData(data);
      setBgColor(data.bgColor || '#2c3e50');
      setTextColor(data.textColor || '#ffffff');
      updateCSSVariables(data.bgColor || '#2c3e50', data.textColor || '#ffffff');
      return data;
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      throw new Error(error.response?.data?.message || error.message || 'Erro ao buscar dados do usuário');
    }
  }, [username, updateCSSVariables]);

  const savePreferences = useCallback(async (bgColor, textColor) => {
    try {
      if (username) {
        await saveUserPreferences(username, { bgColor, textColor });
        setBgColor(bgColor);
        setTextColor(textColor);
        updateCSSVariables(bgColor, textColor);
      } else {
        throw new Error('Username não encontrado no token');
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erro ao salvar preferências');
    }
  }, [username, updateCSSVariables]);

  useEffect(() => {
    updateCSSVariables(bgColor, textColor);
  }, [bgColor, textColor, updateCSSVariables]);

  const handlePasswordUpdate = async (data) => {
    if (data.newPassword && data.confirmPassword && data.newPassword !== data.confirmPassword) {
      setError('confirmPassword', {
        type: 'manual',
        message: 'As senhas não são iguais',
      });
      return false;
    } else {
      clearErrors('confirmPassword');
    }

    try {
      if (username && data.currentPassword && data.newPassword) {
        await updateUserPassword(username, data.currentPassword, data.newPassword);
        return true;
      } else {
        throw new Error('Username não encontrado no token');
      }
    } catch (error) {
      throw new Error(error.message || 'Erro ao atualizar as senhas.');
    }
  };

  const handlePinUpdate = async (pin, currentPin = null) => {
    try {
      if (username && pin) {
        await updateUserPin(username, pin, currentPin);
        const updatedData = await fetchUserData();
        return updatedData;
      } else {
        throw new Error('Username não encontrado no token');
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Erro ao atualizar o PIN.');
    }
  };

  return {
    register,
    handleSubmit,
    errors,
    handlePasswordUpdate,
    handlePinUpdate,
    userData,
    fetchUserData,
    bgColor,
    textColor,
    setBgColor,
    setTextColor,
    savePreferences,
  };
};

export default useProfileForm;